import type { ComponentType } from 'react';
import { ApplicationConfigurationCustomQuestion } from '@zg-rentals/ts-capps-client';
import type { Answers } from '../../../../models/rental-application';
import { getHasOccupants } from '../../../../utils/additionalOccupants';
import { getHasPets, getIsCustomQuestionEnabled } from '../../../../utils/customQuestions';
import { AdditionalOccupantsOverview } from '../../../ApplicationOverview/Accordion/Sections/7Supplemental/AdditionalOccupantsOverview';
import { EmergencyContactOverview } from '../../../ApplicationOverview/Accordion/Sections/7Supplemental/ContactQuestions/EmergencyContactOverview';
import { FinancialContactsOverview } from '../../../ApplicationOverview/Accordion/Sections/7Supplemental/ContactQuestions/FinancialContactsOverview';
import { PersonalReferencesOverview } from '../../../ApplicationOverview/Accordion/Sections/7Supplemental/ContactQuestions/PersonalReferencesOverview';
import { PetRecordsOverview } from '../../../ApplicationOverview/Accordion/Sections/7Supplemental/PetRecordsOverview';
import { VehiclesOverview } from '../../../ApplicationOverview/Accordion/Sections/7Supplemental/VehiclesOverview';
import { AdditionalOccupantsSection } from './AdditionalOccupantsSection';
import { EmergencyContactSection } from './ContactQuestions/EmergencyContactSection';
import { FinancialContactsSection } from './ContactQuestions/FinancialContactsSection';
import { PersonalReferencesSection } from './ContactQuestions/PersonalReferencesSection';
import { EmployerContactOverview, EmployerContactSection, hasEmploymentIncome } from './EmployerContactSection';
import { hasLandlordContact, LandlordContactOverview, LandlordContactSection } from './LandlordContactSection';
import { PetRecordsSection } from './PetRecords/PetRecordsSection';
import { VehiclesSection } from './VehiclesSection';

export type SupplementalSectionProps = {
  applicationId: string;
  answers: Answers;
  onPersist?: () => Promise<void>;
};

export type SupplementalSection = {
  id: string;
  title: string;
  questions: Array<ApplicationConfigurationCustomQuestion>;
  isApplicable?: (answers: Answers) => boolean;
  Component: ComponentType<SupplementalSectionProps>;
  OverviewComponent: ComponentType<SupplementalSectionProps>;
};

export const SUPPLEMENTAL_SECTIONS: Array<SupplementalSection> = [
  {
    id: 'employerContact',
    title: 'Employer contact',
    questions: [
      ApplicationConfigurationCustomQuestion.EMPLOYER_CONTACT_INFO,
      ApplicationConfigurationCustomQuestion.EMPLOYER_CONTACT_PERMISSION,
    ],
    isApplicable: hasEmploymentIncome,
    Component: EmployerContactSection,
    OverviewComponent: EmployerContactOverview,
  },
  {
    id: 'landlordContact',
    title: 'Landlord contact',
    questions: [
      ApplicationConfigurationCustomQuestion.LANDLORD_PHONE,
      ApplicationConfigurationCustomQuestion.LANDLORD_EMAIL,
      ApplicationConfigurationCustomQuestion.LANDLORD_CONTACT_PERMISSION,
    ],
    isApplicable: hasLandlordContact,
    Component: LandlordContactSection,
    OverviewComponent: LandlordContactOverview,
  },
  {
    id: 'pet-records',
    title: 'Veterinary records',
    questions: [
      ApplicationConfigurationCustomQuestion.VETERINARY_RECORDS_UPLOAD,
      ApplicationConfigurationCustomQuestion.PET_PHOTO_UPLOAD,
      ApplicationConfigurationCustomQuestion.ESA_CERTIFICATION_UPLOAD,
    ],
    isApplicable: (answers) =>
      getHasPets(answers) ||
      getIsCustomQuestionEnabled(answers, ApplicationConfigurationCustomQuestion.ESA_CERTIFICATION_UPLOAD),
    Component: PetRecordsSection,
    OverviewComponent: PetRecordsOverview,
  },
  {
    id: 'additional-occupants',
    title: 'Additional occupants',
    questions: [ApplicationConfigurationCustomQuestion.ADDITIONAL_OCCUPANTS],
    isApplicable: getHasOccupants,
    Component: AdditionalOccupantsSection,
    OverviewComponent: AdditionalOccupantsOverview,
  },
  {
    id: 'vehicles',
    title: 'Vehicles',
    questions: [ApplicationConfigurationCustomQuestion.VEHICLES],
    Component: VehiclesSection,
    OverviewComponent: VehiclesOverview,
  },
  {
    id: 'personal-references',
    title: 'Personal references',
    questions: [ApplicationConfigurationCustomQuestion.PERSONAL_REFERENCE],
    Component: PersonalReferencesSection,
    OverviewComponent: PersonalReferencesOverview,
  },
  {
    id: 'emergency-contact',
    title: 'Emergency contact',
    questions: [ApplicationConfigurationCustomQuestion.EMERGENCY_CONTACT],
    Component: EmergencyContactSection,
    OverviewComponent: EmergencyContactOverview,
  },
  {
    id: 'financial-contacts',
    title: 'Financial contacts',
    questions: [
      ApplicationConfigurationCustomQuestion.ACCOUNTANT_CONTACT,
      ApplicationConfigurationCustomQuestion.ATTORNEY_CONTACT,
      ApplicationConfigurationCustomQuestion.BROKERAGE_CONTACT,
    ],
    Component: FinancialContactsSection,
    OverviewComponent: FinancialContactsOverview,
  },
];

export function getVisibleSupplementalSections(answers: Answers | null | undefined) {
  if (!answers) {
    return [];
  }
  return SUPPLEMENTAL_SECTIONS.filter(
    (section) =>
      section.questions.some((question) => getIsCustomQuestionEnabled(answers, question)) &&
      section.isApplicable?.(answers) !== false,
  );
}

export function getSupplementalSectionTitle(section: SupplementalSection, index: number, total: number) {
  return total > 1 ? `${index + 1}. ${section.title}` : section.title;
}
